import TimeIcon from "@mui/icons-material/AccessTimeRounded";
import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import PublishIcon from "@mui/icons-material/LanguageRounded";
import { alpha, Stack, Tooltip, Typography } from "@mui/material";
import type { GridColDef, GridRowClassNameParams, GridRowParams } from "@mui/x-data-grid-premium";
import { useCallback, useMemo, useState } from "react";
import ActionsMenuButton from "@entrilia/shared/components/ActionsMenuButton";
import InlineItemsList from "@entrilia/shared/components/InlineItemsList";
import GridTextHoverLink from "@entrilia/shared/components/grid/GridTextHoverLink";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import {
  DataGridColumnHeadersWithToolbar,
  DataGridHeaderToolbarProvider,
} from "@entrilia/shared/components/grid/DataGridColumnHeadersWithToolbar";
import { getRowSelectionModelFromIds, getSelectedRowIdsFromModel } from "@entrilia/shared/components/grid/gridHelper";
import useMergedSx from "@entrilia/shared/hooks/useMergedSx";
import { distinct } from "@entrilia/shared/utilities/arrayHelper";
import { convertISODate } from "@entrilia/shared/utilities/dateUtils";
import { nameof } from "@entrilia/shared/utilities/typeHelper";
import type { DocumentCollection, InvestorPortalMessageSource } from "../../../api/types/documentCollectionTypes";
import useClientContext from "../../../context/useClientContext";
import UnpublishIcon from "@entrilia/shared/icons/UnpublishIcon";
import { pageRoutes } from "../../../routes";
import { investorPortalMessageSourcesCaptionsMap } from "../../../utilities/enumCaptions";
import CategoryInlineTags from "../../common/CategoryInlineTags";
import { getCheckedGridSx } from "../../common/grid/gridStyles";
import DocumentCollectionPortalStatusTag from "./DocumentCollectionPortalStatusTag";
import DocumentCollectionStatusTag from "./DocumentCollectionStatusTag";
import DocumentCollectionsGridToolbar from "./DocumentCollectionsGridToolbar";
import { useDocumentCollectionsPageContext } from "./DocumentCollectionsPageContext";

const TotalEmailsCell = ({ row }: { row: DocumentCollection }) => {
  if (row.totalEmails) {
    return (
      <Tooltip title={row.notDeliveredEmails > 0 ? "All Emails / Not Delivered Emails" : "All Emails"} arrow>
        <Stack direction="row">
          <Typography>{row.totalEmails}</Typography>
          {row.notDeliveredEmails > 0 && (
            <>
              <Typography>{" / "}</Typography>
              <Typography color="error">{row.notDeliveredEmails}</Typography>
            </>
          )}
        </Stack>
      </Tooltip>
    );
  }

  return (
    <Tooltip title="No Emails" arrow>
      <Typography>-</Typography>
    </Tooltip>
  );
};

const ActionsCell = ({ row }: { row: DocumentCollection }) => {
  const { hasPermissionsToManage, onDownloadDocumentCollection, onChangeDocumentCollectionsStatus } =
    useDocumentCollectionsPageContext();

  const items = [
    {
      icon: <DownloadIcon />,
      label: "Download",
      onClick: () => onDownloadDocumentCollection(row.id),
    },
  ];

  if (hasPermissionsToManage && row.availableOperations.includes("MakeLive")) {
    items.push({
      icon: <PublishIcon />,
      label: "Publish",
      onClick: () => onChangeDocumentCollectionsStatus([row.id], "MakeLive"),
    });
  }

  if (hasPermissionsToManage && row.availableOperations.includes("MakePreview")) {
    items.push({
      icon: <UnpublishIcon />,
      label: "Unpublish",
      onClick: () => onChangeDocumentCollectionsStatus([row.id], "MakePreview"),
    });
  }

  if (hasPermissionsToManage && row.availableOperations.includes("Revoke")) {
    items.push({
      icon: <DeleteIcon color="error" />,
      label: "Delete",
      onClick: () => onChangeDocumentCollectionsStatus([row.id], "Revoke"),
    });
  }

  return <ActionsMenuButton items={items} />;
};

const NameCell = ({ row }: { row: DocumentCollection }) => {
  const { clientCode } = useClientContext();

  return (
    <GridTextHoverLink
      to={`/${clientCode}/${pageRoutes.investorRelations}/${pageRoutes.documentCollections}/${row.id}`}
      text={row.name}
    />
  );
};

const columnDefinitions: GridColDef<DocumentCollection>[] = [
  {
    field: nameof<DocumentCollection>("no"),
    type: "number",
    headerName: "ID",
    headerAlign: "center",
    align: "center",
    flex: 0.5,
    minWidth: 100,
  },
  {
    field: nameof<DocumentCollection>("name"),
    headerName: "Name",
    flex: 3,
    minWidth: 160,
    renderCell: ({ row }) => <NameCell row={row} />,
  },
  {
    field: nameof<DocumentCollection>("funds"),
    headerName: "Fund",
    flex: 3,
    minWidth: 160,
    renderCell: ({ row }) =>
      row.funds.length === 0 ? (
        "-"
      ) : (
        <InlineItemsList displayCount={1} items={distinct(row.funds.map((f) => f.name))} propGetter={(name) => name} />
      ),
  },
  {
    field: nameof<DocumentCollection>("categories"),
    headerName: "Category",
    flex: 3,
    minWidth: 256,
    renderCell: ({ row }) => (
      <CategoryInlineTags externalCategoryIds={row.categoryIds} maxDisplayedCategories={2} noWrap />
    ),
  },
  {
    field: nameof<DocumentCollection>("messageLevel"),
    headerName: "Level",
    flex: 1,
    minWidth: 100,
  },
  {
    field: nameof<DocumentCollection>("publishDate"),
    headerName: "Publish Date",
    flex: 1.5,
    minWidth: 120,
    valueFormatter: (value: string) => convertISODate(value),
  },
  {
    field: nameof<DocumentCollection>("portalPublishStatus"),
    headerName: "Investor Portal",
    flex: 1.5,
    minWidth: 160,
    renderCell: ({ row }) => <DocumentCollectionPortalStatusTag portalPublishStatus={row.portalPublishStatus} />,
  },
  {
    field: nameof<DocumentCollection>("status"),
    headerName: "Status",
    flex: 1.5,
    minWidth: 128,
    renderCell: ({ row }) => (
      <>
        <DocumentCollectionStatusTag status={row.status} />
        {row.isStatusChangeScheduled && (
          <Tooltip arrow title="Status change in progress">
            <TimeIcon color="action" sx={{ ml: 1 }} />
          </Tooltip>
        )}
      </>
    ),
  },
  {
    field: nameof<DocumentCollection>("messageSource"),
    headerName: "Source",
    flex: 1.5,
    minWidth: 120,
    valueFormatter: (value: InvestorPortalMessageSource) => investorPortalMessageSourcesCaptionsMap[value],
  },
  {
    field: nameof<DocumentCollection>("numberOfDocuments"),
    type: "number",
    headerName: "Documents",
    flex: 1,
    minWidth: 100,
  },
  {
    field: nameof<DocumentCollection>("totalEmails"),
    headerName: "Emails",
    headerAlign: "right",
    align: "right",
    flex: 1,
    minWidth: 112,
    renderCell: ({ row }) => <TotalEmailsCell row={row} />,
  },
  {
    field: "actions",
    headerName: "Actions",
    resizable: false,
    width: 72,
    align: "right",
    headerAlign: "right",
    cellClassName: "grid-row-actions",
    renderCell: ({ row }) => <ActionsCell row={row} />,
  },
];

interface Props {
  documentCollections: DocumentCollection[];
  loading: boolean;
  highlightedRowId?: string;
}

const isRowSelectable = ({ row }: GridRowParams<DocumentCollection>) => row.messageSource === "DataImport";

const DocumentCollectionsGrid = ({ documentCollections, loading, highlightedRowId }: Props) => {
  const { investorPortalApp } = useClientContext();
  const { hasPermissionsToManage } = useDocumentCollectionsPageContext();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const showToolbar = useMemo(() => selectedIds.length > 0, [selectedIds.length]);

  const columnVisibilityModel = useMemo(
    () => ({ [nameof<DocumentCollection>("portalPublishStatus")]: Boolean(investorPortalApp) }),
    [investorPortalApp]
  );

  const sx = useMergedSx(
    (theme) => getCheckedGridSx(theme),
    (theme) => ({
      ".MuiDataGrid-row": {
        "&.is-highlighted": {
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
        },
      },
    })
  );

  const getRowClassName = useCallback(
    ({ row }: GridRowClassNameParams<DocumentCollection>) => (row.id === highlightedRowId ? "is-highlighted" : ""),
    [highlightedRowId]
  );

  return (
    <DataGridHeaderToolbarProvider
      showToolbar={showToolbar}
      selection={[selectedIds, setSelectedIds]}
      actions={<DocumentCollectionsGridToolbar selectedIds={selectedIds} allRows={documentCollections} />}
    >
      <DataGrid<DocumentCollection>
        columns={columnDefinitions}
        columnVisibilityModel={columnVisibilityModel}
        rows={documentCollections}
        loading={loading}
        checkboxSelection={hasPermissionsToManage}
        isRowSelectable={isRowSelectable}
        rowSelectionModel={getRowSelectionModelFromIds(selectedIds)}
        onRowSelectionModelChange={(selection) => setSelectedIds(getSelectedRowIdsFromModel(selection))}
        noRowsText="No document collections"
        getRowClassName={selectedIds.length === 0 ? getRowClassName : undefined}
        disableColumnReorder
        disableColumnSorting
        sx={sx}
        slots={{ columnHeaders: DataGridColumnHeadersWithToolbar }}
      />
    </DataGridHeaderToolbarProvider>
  );
};

export default DocumentCollectionsGrid;
